/**
 * Copy/Share Utility
 *
 * Generic clipboard-copy and OS-share-sheet mechanism, shared by every
 * detail-panel dropdown that offers Copy/Share (guides, partner notes).
 * Content-specific formatting (what text to build) lives with each
 * consumer, not here — see guideOptionsHelper.js and partnerNotesDrillHelper.js.
 *
 * @module utils/shareContent
 */

/**
 * Copy text to the clipboard, surfacing a toast on success/failure.
 * @param {string} text - Plain text to copy
 * @param {Object} [opts]
 * @param {string} [opts.successMessage='Copied to clipboard']
 * @returns {Promise<boolean>} Whether the copy succeeded
 */
export async function copyToClipboard(text, { successMessage = 'Copied to clipboard' } = {}) {
  if (!text || !text.trim()) return false;

  try {
    await navigator.clipboard.writeText(text);
    window.notificationManager?.showSuccess(successMessage);
    return true;
  } catch (error) {
    console.error('[shareContent] Copy failed:', error);
    window.notificationManager?.showError('Could not copy to clipboard.');
    return false;
  }
}

/**
 * Share via the OS share sheet, falling back to clipboard copy when
 * `navigator.share` is unavailable or fails for a reason other than the
 * user cancelling.
 * @param {Object} data
 * @param {string} [data.title]
 * @param {string} data.text
 * @param {string} [data.url]
 * @param {Object} [opts]
 * @param {string} [opts.copySuccessMessage] - Passed through to the copy fallback
 * @returns {Promise<boolean>} Whether the share (or fallback copy) succeeded
 */
export async function shareOrCopy({ title, text, url }, { copySuccessMessage } = {}) {
  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ title, text, url });
      return true; // OS share sheet owns its own confirmation UI — no toast here
    } catch (error) {
      if (error?.name === 'AbortError') return false; // user cancelled — no error toast
      // Fall through to the clipboard fallback below.
    }
  }
  return copyToClipboard(text, { successMessage: copySuccessMessage });
}

/**
 * Strip the small markdown subset used by guide/note content down to plain
 * text, for the clipboard/share sheet. Operates on the raw string — must NOT
 * escape HTML entities (unlike the HTML-output `renderMarkdown()` methods
 * this mirrors), since the output here is plain text, not markup.
 *
 * Numbered-list lines (`1. Step one`) are deliberately left untouched (no
 * regex matches them) — they pass through with their numbering intact.
 * @param {string} markdown - Raw markdown-subset text
 * @returns {string} Plain text
 */
export function markdownToPlainText(markdown) {
  if (!markdown) return '';

  let text = markdown;
  text = text.replace(/^### (.+)$/gm, '$1');
  text = text.replace(/^## (.+)$/gm, '$1');
  text = text.replace(/\*\*([^*]+)\*\*/g, '$1');
  text = text.replace(/\*([^*]+)\*/g, '$1');
  text = text.replace(/^- (.+)$/gm, '- $1');

  return text.trim();
}
